import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Joyride, { STATUS, type Step } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";

import { postUserTourShown } from "@/services/TourGuide";
import { selectShowTour, setShowTour } from "@/store/roleSlice";

interface TourGuideProps {
  userPrincipalName: string;
  flag?: number;
}

const TourGuide: React.FC<TourGuideProps> = ({ userPrincipalName, flag = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const reduxTrigger = useSelector(selectShowTour);

  const [runTour, setRunTour] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  useEffect(() => {
    if (reduxTrigger || flag) {
      setRunTour(false);

      const timer = setTimeout(() => {
        setTourKey((k) => k + 1);
        setRunTour(true);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [reduxTrigger, flag]);

  const steps: Step[] = useMemo(
    () => [
      {
        target: ".timezone-bar",
        content: t("components.tour.timezoneBar", {
          defaultValue: "This bar shows current times in key locations across Africa.",
        }),
        disableBeacon: true,
      },
      {
        target: ".search-icon",
        content: t("components.tour.searchIcon", {
          defaultValue: "Click here to access the search functionality.",
        }),
      },
      {
        target: ".notification-icon",
        content: t("components.tour.notificationIcon", {
          defaultValue: "View your notifications here.",
        }),
      },
      {
        target: ".language-switcher",
        content: t("components.tour.languageSwitcher", {
          defaultValue: "Use this menu to change the dashboard language.",
        }),
      },
      {
        target: ".profile-menu",
        content: t("components.tour.profileMenu", {
          defaultValue: "Use this menu to manage your profile and settings.",
        }),
      },
    ],
    [t]
  );

  const onJoyrideCallback = async ({ status, action }: { status: string; action: string }) => {
    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      action === "close" ||
      action === "skip"
    ) {
      setRunTour(false);

      try {
        await postUserTourShown(userPrincipalName);
      } catch {
        // ignore errors
      }

      dispatch(setShowTour(false));
      document.body.style.overflow = "";
    }
  };

  const joyrideProps = {
    steps,
    run: runTour,
    continuous: true,
    scrollToFirstStep: true,
    showSkipButton: true,
    hideCloseButton: true,
    disableCloseOnEsc: true,
    disableOverlayClose: false,
    spotlightPadding: 6,
    styles: {
      options: {
        zIndex: 10000,
        primaryColor: "#007bff",
      },
    },
    locale: {
      last: t("components.tour.finish", { defaultValue: "Finish" }),
      next: t("components.tour.next", { defaultValue: "Next" }),
      back: t("components.tour.previous", {
        defaultValue: "Previous",
      }),
    },
    callback: onJoyrideCallback,
  } as any; // keep this only if your Joyride types mismatch

  return <Joyride key={tourKey} {...joyrideProps} />;
};

export default TourGuide;
